/**
 * 日本語（JIS）配列のキーボードを前提に、vimの打鍵表記を「実際に押す物理キー」へ直す。
 *
 * 本道場の学習者は日本語配列で稽古する。JIS配列では `$` は Shift + 4、`*` は Shift + :、
 * `?` は Shift + / であり、`^` `@` `:` は US配列と違って**単独で打てる**。
 * 「画面に出ているこの記号を、どのキーで打つのか」が分からないまま詰まることを防ぐため、
 * 打鍵の帯とキー一覧はこの表を通して物理キーの形で描く。
 */

export interface PhysicalKey {
  /** キートップの刻印（例: `4` / `A` / `Shift` / `Enter`）。 */
  readonly legend: string;
  /** Shift・Ctrl のような修飾キーか（幅広の札で描く）。 */
  readonly modifier?: boolean;
  /** Enter・Esc のような名前付きの幅広キーか。 */
  readonly wide?: boolean;
}

/** JIS配列で Shift との同時押しが要る文字 → キートップの刻印。 */
const JIS_SHIFTED: Readonly<Record<string, string>> = {
  "!": "1",
  '"': "2",
  "#": "3",
  $: "4",
  "%": "5",
  "&": "6",
  "'": "7",
  "(": "8",
  ")": "9",
  "=": "-",
  "~": "^",
  "|": "\\",
  "`": "@",
  "{": "[",
  "+": ";",
  "*": ":",
  "}": "]",
  "<": ",",
  ">": ".",
  "?": "/",
  _: "\\",
};

/** 名前付きキーの表記 → キートップの刻印。 */
const NAMED_KEYS: Readonly<Record<string, string>> = {
  "<Esc>": "Esc",
  "<CR>": "Enter",
  "<BS>": "Back",
  "<Del>": "Delete",
  "<Space>": "Space",
  Shift: "Shift",
  Control: "Ctrl",
  Alt: "Alt",
  Meta: "Meta",
};

/** 打鍵の帯のラベル（`dw` `<C-r>` `Shift` など）を1打ずつに切り分ける。 */
export function splitNotations(label: string): readonly string[] {
  if (NAMED_KEYS[label] !== undefined) {
    return [label];
  }
  return label.match(/<[^>]+>|./g) ?? [];
}

/**
 * 1打（`w` `$` `<C-r>` など）を、JIS配列で実際に押す物理キーの並びへ直す。
 * Shift・Ctrl が要る打鍵は、修飾キーの札を先頭に置いた2枚組で返す。
 */
export function physicalKeysFor(notation: string): readonly PhysicalKey[] {
  const named = NAMED_KEYS[notation];
  if (named !== undefined) {
    const isModifier =
      notation === "Shift" || notation === "Control" || notation === "Alt" || notation === "Meta";
    return [{ legend: named, ...(isModifier ? { modifier: true } : { wide: true }) }];
  }

  const ctrl = /^<C-(.+)>$/.exec(notation);
  if (ctrl?.[1] !== undefined) {
    return [{ legend: "Ctrl", modifier: true }, ...physicalKeysFor(ctrl[1])];
  }

  const shifted = JIS_SHIFTED[notation];
  if (shifted !== undefined) {
    return [{ legend: "Shift", modifier: true }, { legend: shifted }];
  }

  // 英字は刻印どおり大文字で描く。大文字を打つには Shift が要る。
  if (/^[a-z]$/.test(notation)) {
    return [{ legend: notation.toUpperCase() }];
  }
  if (/^[A-Z]$/.test(notation)) {
    return [{ legend: "Shift", modifier: true }, { legend: notation }];
  }

  return [{ legend: notation }];
}

/** JIS配列で Shift との同時押しが要る打鍵か。 */
export function needsShiftOnJis(notation: string): boolean {
  return physicalKeysFor(notation).some((key) => key.legend === "Shift");
}
